import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { gsap } from 'gsap';

const StickyTextSection = () => {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);
  const textRefs = useRef([]);

  const texts = [
    "Printed to order.",
    "Worldwide shipping.",
    "Produced in Europe."
  ];

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const { data, error } = await supabase
          .from('site_images')
          .select('image_url')
          .eq('section', 'sticky-text')
          .single();

        if (error) throw error;
        setBackgroundImage(data.image_url);
      } catch (error) {
        console.error('Error fetching background image:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();
  }, []);

  // GSAP animation driven by live scroll position — no ScrollTrigger position caching,
  // so it's immune to layout shifts caused by other async components loading.
  useEffect(() => {
    if (loading) return;

    const section = sectionRef.current;
    const els = textRefs.current;
    const totalTexts = texts.length;
    const segSize = 1 / totalTexts;

    // Set all texts to initial state (below, invisible)
    els.forEach(el => {
      if (el) gsap.set(el, { yPercent: 50, opacity: 0 });
    });

    // Paused timeline — we scrub its progress manually using live DOM measurements
    const tl = gsap.timeline({ paused: true });

    els.forEach((el, i) => {
      if (!el) return;

      const segStart = i * segSize;
      const enterDur = segSize * 0.3;
      const holdDur = segSize * 0.4;
      const exitDur = segSize * 0.3;
      const isLast = i === totalTexts - 1;

      tl.to(el, { yPercent: 0, opacity: 1, ease: 'none', duration: enterDur }, segStart);
      if (!isLast) {
        tl.to(el, { yPercent: -50, opacity: 0, ease: 'none', duration: exitDur }, segStart + enterDur + holdDur);
      }
    });

    // Drive timeline progress from live getBoundingClientRect() on every scroll.
    // This is always accurate regardless of when surrounding content loads.
    const updateProgress = () => {
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollableDistance = sectionHeight - windowHeight;
      const scrolledAmount = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolledAmount / scrollableDistance));
      tl.progress(progress);
    };

    window.addEventListener('scroll', updateProgress, { passive: true });

    const rafId = requestAnimationFrame(() => {
      if (window.lenis) window.lenis.on('scroll', updateProgress);
      updateProgress();
    });

    return () => {
      window.removeEventListener('scroll', updateProgress);
      cancelAnimationFrame(rafId);
      if (window.lenis) window.lenis.off('scroll', updateProgress);
      tl.kill();
    };
  }, [loading]);

  if (loading) {
    return (
      <section style={{
        height: '100vh',
        backgroundColor: '#0D0D0D',
      }} />
    );
  }

  return (
    <section
      ref={sectionRef}
      style={{
        height: `${texts.length * 100}vh`,
        position: 'relative',
        backgroundColor: '#0D0D0D',
      }}
    >
      {/* Sticky Container */}
      <div style={{
        position: 'sticky',
        top: 0,
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        marginBottom: '10px'
      }}>
        {/* Background Image */}
        {backgroundImage && (
          <img
            src={backgroundImage}
            alt="Background"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}

        {/* Text Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {texts.map((text, index) => (
            <p
              key={index}
              ref={el => { textRefs.current[index] = el; }}
              style={{
                position: 'absolute',
                fontSize: '32px',
                fontWeight: '400',
                color: '#F1F1F1',
                fontFamily: "'TASA Orbiter Display', sans-serif",
                letterSpacing: '-0.01em',
                willChange: 'transform, opacity',
              }}
            >
              {text}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StickyTextSection;
